<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    title="フロー設定"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <!-- BODY -->
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- 接続先VNコネクト名（W番号）/接続先VPN（VPNコード） -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="接続先VNコネクト名(W番号)/ 接続先VPN(VPNコード)"
                      :isInfo="false"
                    />
                    <validation-provider
                      name="selectedDstInfo"
                      v-model="form.selectedDstInfo"
                      label="接続先VNコネクト名(W番号)/ 接続先VPN(VPNコード)"
                      :rules="{ required: true }"
                      v-slot="{ componentField, errorMessage }"
                    >
                      <app-multi-select
                        :options="dstOptions"
                        trackBy="wnumberOrVpnCode"
                        :custom-label="dstInfoSelectLabel"
                        group-label="dstLabel"
                        group-values="dstValues"
                        v-bind="componentField"
                        :error-messages="errorMessage"
                      >
                        <template #option="{ option }">
                          <template v-if="option.$isLabel">
                            <div class="multiselect-thead">
                              <p class="v-col-3 pa-0">
                                {{ option.$groupLabel.wnumberOrVpnCode }}
                              </p>
                              <p class="v-col-8 pa-0">
                                {{ option.$groupLabel.vnConnectOrL3vpn }}
                              </p>
                            </div>
                          </template>

                          <div class="multiselect-tbody" v-else>
                            <p class="v-col-3 pa-0">
                              {{ option.wnumberOrVpnCode }}
                            </p>
                            <p class="v-col-8 pa-0">
                              {{ option.vnConnectOrL3vpn }}
                            </p>
                          </div>
                        </template>
                      </app-multi-select>
                    </validation-provider>
                  </v-col>
                </v-row>
                <!-- 送信元アドレス -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="送信元アドレス"
                      :isRequired="form.radioSrcAddress !== null"
                      :isInfo="form.radioSrcAddress !== null"
                      :msg="
                        $filter.message(
                          'info_cidr_ip_address_of_network_address',
                          {
                            ipaddress1: '192.168.0.0/24',
                            genre: '送信元アドレス',
                            subnet1: '1',
                            subnet2: '31',
                            ipaddress2: '192.168.0.1/32',
                          }
                        )
                      "
                    />
                    <v-radio-group
                      inline
                      v-model="form.radioSrcAddress"
                      hide-details
                    >
                      <v-radio
                        v-for="option in addrOptions"
                        :key="option.text"
                        :label="option.text"
                        :value="option.value"
                      />
                    </v-radio-group>
                    <template v-if="form.radioSrcAddress !== null">
                      <validation-provider
                        name="srcAddress"
                        v-model="form.srcAddress"
                        label="送信元アドレス"
                        :rules="{
                          required: true,
                          is_ip_subnet: { min: 1, max: 32 },
                          is_not_loopback_ip: true,
                          is_not_multicast_ip: true,
                          is_not_class_e_ip: true,
                          is_not_broadcast_ip: true,
                          is_network_address: true,
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                          :placeholder="
                            $filter.message(
                              'placeholder_ip_address_of_private_network_address'
                            )
                          "
                        />
                      </validation-provider>
                    </template>
                    <template v-else>
                      <app-form-input disabled />
                    </template>
                  </v-col>
                </v-row>
                <!-- 宛先アドレス -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="宛先アドレス"
                      :isRequired="form.radioDstAddress !== null"
                      :isInfo="form.radioDstAddress !== null"
                      :msg="
                        $filter.message(
                          'info_cidr_ip_address_of_network_address',
                          {
                            ipaddress1: '192.168.0.0/24',
                            genre: '宛先アドレス',
                            subnet1: '1',
                            subnet2: '31',
                            ipaddress2: '192.168.0.1/32',
                          }
                        )
                      "
                    />
                    <v-radio-group
                      inline
                      v-model="form.radioDstAddress"
                      hide-details
                    >
                      <v-radio
                        v-for="option in addrOptions"
                        :key="option.text"
                        :label="option.text"
                        :value="option.value"
                      />
                    </v-radio-group>
                    <template v-if="form.radioDstAddress !== null">
                      <validation-provider
                        name="dstAddress"
                        v-model="form.dstAddress"
                        label="宛先アドレス"
                        :rules="{
                          required: true,
                          is_ip_subnet: { min: 1, max: 32 },
                          is_not_loopback_ip: true,
                          is_not_multicast_ip: true,
                          is_not_class_e_ip: true,
                          is_not_broadcast_ip: true,
                          is_network_address: true,
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                          :placeholder="
                            $filter.message(
                              'placeholder_ip_address_of_private_network_address'
                            )
                          "
                        />
                      </validation-provider>
                    </template>
                    <template v-else>
                      <app-form-input disabled />
                    </template>
                  </v-col>
                </v-row>
                <!-- 備考 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="備考"
                      :msg="
                        $filter.message('info_max_length', { maxLength: '20' })
                      "
                      :isRequired="false"
                    />
                    <validation-provider
                      name="description"
                      v-model="form.description"
                      label="備考"
                      :rules="{
                        max: { length: 20 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">設定</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { FlowEntity } from "@/modals/site/SiteReference.vue";
import cloneDeep from "lodash/cloneDeep";
import type { VNetworkBrief } from "@/apis/VirtualNetworkApi";
import type { IfStatus, VNConnect } from "@/apis/VNConnectApi";
import { VueFinalModal } from "vue-final-modal";

type dstInfo = {
  dstLabel: { vnConnectOrL3vpn: string; wnumberOrVpnCode: string };
  dstValues: DstInfoEntity[];
};

export default defineComponent({
  name: "SiteFlowEdit",
  props: {
    /** flow詳細、変更時のみ指定 */
    flow: {
      type: Object as PropType<FlowEntity>,
      required: false,
    },
    enumber: {
      type: String as PropType<string>,
      required: true,
    },
    vlanIdType: {
      type: String as PropType<"UNTAG" | "OTHER" | "SPECIFIED">,
      required: true,
    },
    vlanId: {
      type: Number as PropType<number>,
      required: false,
      default: null,
    },
  },
  data() {
    const flowClone = cloneDeep(this.flow) as FlowEntity | undefined;
    return {
      isLoaded: false,
      addrOptions: [
        { value: null, text: "any" },
        { value: "address", text: "アドレス指定" },
      ],
      form: flowClone
        ? {
            // 変更
            selectedDstInfo: null as DstInfoEntity | null | undefined,
            srcAddress: flowClone.srcAddress,
            dstAddress: flowClone.dstAddress,
            description: flowClone.description,
            radioSrcAddress: flowClone.srcAddress ? "address" : null,
            radioDstAddress: flowClone.dstAddress ? "address" : null,
          }
        : {
            // 追加
            selectedDstInfo: null as DstInfoEntity | null | undefined,
            srcAddress: null as string | null,
            dstAddress: null as string | null,
            description: null as string | null,
            radioSrcAddress: null as string | null,
            radioDstAddress: null as string | null,
          },
      flowClone: flowClone,
      // APIの取得値を持ち
      availableVnNetwork: [] as VNetworkBrief[],
      vnConnects: [] as VNConnect[],
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },

  computed: {
    /** 変更の場合、true */
    isEdit(): boolean {
      return !!this.flow;
    },
    /** プルダウンの選択肢リスト */
    dstData(): DstInfoEntity[] {
      // 接続先VNコネクト名(W番号)/ 接続先VPN(VPNコード)の選択肢のデータ生成
      const selectOption = this.availableVnNetwork.map((e) => ({
        vnConnectOrL3vpn: e.vnConnectName ? e.vnConnectName : "L3VPN",
        wnumberOrVpnCode: e.wnumber || e.vpnCode || "",
        l2vn: e.wnumber
          ? !!(
              this.vnConnects.find((v) => v.wnumber === e.wnumber)?.vnType ===
                "L2" &&
              this.vnConnects.find((v) => v.wnumber === e.wnumber)?.vnName
            )
          : false,
        //VN面はL3の場合、有効/無効は必ずnullに指定する
        isVnConnectStatus: e.vnConnectName
          ? e.wnumber
            ? this.vnConnects.find((v) => v.wnumber === e.wnumber)
                ?.isVnConnectStatus
            : this.vnConnects.find((v) => v.vpnVnCode === e.vpnCode)
                ?.isVnConnectStatus
          : null,
        //VN面はL3の場合、IF状態は必ずnullに指定する
        ifStatus: e.vnConnectName
          ? e.wnumber
            ? this.vnConnects.find((v) => v.wnumber === e.wnumber)?.ifStatus
            : this.vnConnects.find((v) => v.vpnVnCode === e.vpnCode)?.ifStatus
          : null,
      }));

      /** VPNコード(１件のみあるはず)は一番上に表示、W番号は降順ソートで表示 */
      const vpn = selectOption.filter(
        (v) => v.wnumberOrVpnCode.charAt(0) === "B"
      );
      const wnumberList = selectOption
        .filter((v) => v.wnumberOrVpnCode.charAt(0) !== "B")
        .sortBy(["wnumberOrVpnCode", "desc"]);
      return [...vpn, ...wnumberList];
    },
    /** 接続先VNコネクト名(W番号)/ 接続先VPN(VPNコード)の選択肢 */
    dstOptions(): dstInfo[] {
      return this.dstData.length !== 0
        ? [
            {
              dstLabel: {
                wnumberOrVpnCode: "W番号/VPNコード",
                vnConnectOrL3vpn: "VNコネクト名/L3VPN",
              },
              dstValues: this.dstData,
            },
          ]
        : [];
    },
    /** 画面が採用された送信元アドレスアドレス */
    getSubmitScrAddress(): string | null {
      return !this.form.radioSrcAddress ? null : this.form.srcAddress;
    },
    /** 画面が採用された宛先アドレスアドレス */
    getSubmitDstAddress(): string | null {
      return !this.form.radioDstAddress ? null : this.form.dstAddress;
    },
  },

  methods: {
    async load() {
      // フロー設定可能なVN回線の一覧を取得
      // サイトに紐づいた回線（E番号)がL3かつサイトがUntag以外のデータがこない
      this.availableVnNetwork = (
        await this.$api.virtualNetwork.getFlowAvailableVnNetwork({
          enumber: this.enumber,
          vlanIdType: this.vlanIdType,
          vlanId: this.vlanId,
        })
      ).targetList;

      // VNコネクトを一覧取得
      this.vnConnects = (
        await this.$api.vNConnect.getVNConnects()
      ).vnConnectList;

      if (this.isEdit) {
        // 編集の場合
        this.form.selectedDstInfo = this.dstData.find(
          (v) =>
            v.wnumberOrVpnCode === this.flow?.vpnCode ||
            v.wnumberOrVpnCode === this.flow?.wnumber
        );
      }
    },

    /** 接続先検索付きプルダウンの選択後表示形式 */
    dstInfoSelectLabel({ vnConnectOrL3vpn, wnumberOrVpnCode }: DstInfoEntity) {
      return `${vnConnectOrL3vpn}(${wnumberOrVpnCode})`;
    },

    /** フロー設定 */
    async submit() {
      if (!this.isEdit) {
        const flow = {
          flowSeq: null,
          vnConnectName:
            this.form.selectedDstInfo!.vnConnectOrL3vpn !== "L3VPN"
              ? this.form.selectedDstInfo!.vnConnectOrL3vpn
              : null,
          wnumber:
            this.form.selectedDstInfo!.vnConnectOrL3vpn !== "L3VPN"
              ? this.form.selectedDstInfo!.wnumberOrVpnCode
              : null,
          vpnCode:
            this.form.selectedDstInfo!.vnConnectOrL3vpn === "L3VPN"
              ? this.form.selectedDstInfo!.wnumberOrVpnCode
              : null,
          srcAddress: this.getSubmitScrAddress,
          dstAddress: this.getSubmitDstAddress,
          // VNコネクトの状態は有効(true)の場合:VALID、無効(false)の場合:INVALID
          flowStatus:
            this.form.selectedDstInfo!.isVnConnectStatus === null
              ? null
              : this.form.selectedDstInfo!.isVnConnectStatus
                ? "VALID"
                : "INVALID",
          ifStatus: this.form.selectedDstInfo!.ifStatus,
          description: this.form.description,
        };
        (
          this.$refs.modal as typeof VueFinalModal & {
            ok: (e: unknown) => void;
          }
        ).ok(flow);
      } else {
        const flow = {
          ...this.flowClone,
          vnConnectName:
            this.form.selectedDstInfo!.vnConnectOrL3vpn !== "L3VPN"
              ? this.form.selectedDstInfo!.vnConnectOrL3vpn
              : null,
          wnumber:
            this.form.selectedDstInfo!.vnConnectOrL3vpn !== "L3VPN"
              ? this.form.selectedDstInfo!.wnumberOrVpnCode
              : null,
          vpnCode:
            this.form.selectedDstInfo!.vnConnectOrL3vpn === "L3VPN"
              ? this.form.selectedDstInfo!.wnumberOrVpnCode
              : null,
          srcAddress: this.getSubmitScrAddress,
          dstAddress: this.getSubmitDstAddress,
          // VNコネクトの状態は有効(true)の場合:VALID、無効(false)の場合:INVALID
          flowStatus:
            this.form.selectedDstInfo!.isVnConnectStatus === null
              ? null
              : this.form.selectedDstInfo!.isVnConnectStatus
                ? "VALID"
                : "INVALID",
          ifStatus: this.form.selectedDstInfo!.ifStatus,
          description: this.form.description,
        };
        (
          this.$refs.modal as typeof VueFinalModal & {
            ok: (e: unknown) => void;
          }
        ).ok(flow);
      }
    },
  },
});
export interface DstInfoEntity {
  /** VNコネクト名/L3VPN */
  vnConnectOrL3vpn: string;
  /** W番号/VPNコード */
  wnumberOrVpnCode: string;
  /** 追加時、新規で表示する項目 */
  l2vn: boolean | undefined;
  isVnConnectStatus: boolean | undefined | null;
  ifStatus: IfStatus | undefined | null;
}
</script>
